import React, { useEffect, useState } from 'react';
import { Container, Typography, Paper, Box, Chip, Button, IconButton, Tooltip, Switch, Table, TableHead, TableRow, TableCell, TableBody, CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import DevicesIcon from '@mui/icons-material/Devices';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import { useTranslation } from 'react-i18next';
import Menu from '../components/Menu';
import PaymentDialog from '../components/PaymentDialog';
import { useAuth } from '../hooks/useAuth';
import { useNotifications } from '../context/NotificationContext';
import api from '../utils/apiClient';
import { API_BASE } from '../utils/apiConfig';
import { getLocale } from '../utils/dateLocale';
import { normalizeRole, isAdminOrSuperAdmin } from '../utils/roles';
import { resolveBillName } from '../utils/billName';
import { formatBillAmount } from '../utils/billAmount';

const Subscription = () => {
  const { t } = useTranslation(['billing', 'profile', 'common']);
  const theme = useTheme();
  const { user } = useAuth();
  const { showError } = useNotifications();
  const isAdmin = isAdminOrSuperAdmin(user?.role);
  const isHoster = normalizeRole(user?.role) === 'hoster';
  const isManagerOrAdmin = isAdmin || isHoster;

  // Курс для конвертации RUB-сумм счёта в USD-эквивалент. Берём из самого ответа
  // /subscription -- бэк уже отдаёт total_usd и total_rub для блока «Ваш тариф»,
  // так что pending_bill и история считаются по тому же курсу, что и тариф.
  const computeRubRate = (subData) => {
    const usd = Number(subData?.total_usd);
    const rub = Number(subData?.total_rub);
    if (!Number.isFinite(usd) || usd <= 0 || !Number.isFinite(rub) || rub <= 0) return null;
    return rub / usd;
  };

  const formatBillAmountParts = (amount, currency, subData, opts = {}) => {
    const cur = String(currency || '').toUpperCase() || 'USD';
    const num = Number(amount) || 0;
    const rate = computeRubRate(subData);
    const rubSign = t('subRubSign');
    const formatRub = (v) => v.toLocaleString(getLocale());
    if (cur === 'USDT') return { primary: `${num} USDT`, secondary: null };
    if (cur === 'RUB') {
      if (rate) {
        return { primary: `$${Math.round(num / rate)}`, secondary: `${formatRub(num)} ${rubSign}` };
      }
      return { primary: `${formatRub(num)} ${rubSign}`, secondary: null };
    }
    // USD-legacy pending: рубли = data.total_rub (сумма тарифа), сохраняем прежний рендер.
    if (opts.usdRubFromTariff) {
      const tariffRub = Number(subData?.total_rub);
      if (Number.isFinite(tariffRub) && tariffRub > 0) {
        return { primary: `$${num}`, secondary: `~${formatRub(tariffRub)} ${rubSign}` };
      }
    }
    if (rate) {
      return { primary: `$${num}`, secondary: `~${formatRub(Math.round(num * rate))} ${rubSign}` };
    }
    return { primary: `$${num}`, secondary: null };
  };

  const renderBillAmount = (amount, currency, subData, opts) => {
    const { primary, secondary } = formatBillAmountParts(amount, currency, subData, opts);
    return (
      <>
        {primary}
        {secondary && (
          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            ({secondary})
          </Typography>
        )}
      </>
    );
  };
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [hosterData, setHosterData] = useState(null);
  const [hosterLoading, setHosterLoading] = useState(false);
  const [hosterError, setHosterError] = useState(null);
  const [hosterPaymentOpen, setHosterPaymentOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [freePeriodDialog, setFreePeriodDialog] = useState({ open: false, userId: null, name: '' });
  const [freePeriodDays, setFreePeriodDays] = useState(7);
  const [freePeriodMsg, setFreePeriodMsg] = useState(null);
  const [monitoringDialog, setMonitoringDialog] = useState({ open: false, userId: null, email: '', targetDisabled: false });
  const [monitoringBusy, setMonitoringBusy] = useState(false);
  const [clientsSearchQuery, setClientsSearchQuery] = useState('');

  const fetchSubscription = async () => {
    try {
      setLoading(true);
      const res = await api.get(`${API_BASE}/subscription`);
      setData(res.data);
    } catch (e) {
      console.error('Subscription: ошибка загрузки', e);
      setError(t('subLoadError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchHosterSubscription = async () => {
    try {
      setHosterLoading(true);
      const res = await api.get(`${API_BASE}/subscription/hoster`);
      setHosterData(res.data);
    } catch (e) {
      console.error('Subscription hoster: ошибка загрузки', e);
      setHosterError(t('subLoadError'));
    } finally {
      setHosterLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscription();
  }, []);

  useEffect(() => {
    if (!isHoster) return;
    fetchHosterSubscription();
  }, [isHoster]);

  useEffect(() => {
    if (!isAdmin) return;
    const fetchClients = async () => {
      try {
        setClientsLoading(true);
        const res = await api.get(`${API_BASE}/subscription/clients`);
        if (res.data?.success) setClients(res.data.clients || []);
      } catch (e) {
        console.error('Subscription clients: ошибка загрузки', e);
      } finally {
        setClientsLoading(false);
      }
    };
    fetchClients();
  }, [isAdmin]);

  const handleSetFreePeriod = async () => {
    try {
      await api.patch(`${API_BASE}/subscription/free-period`, {
        user_id: freePeriodDialog.userId,
        days: parseInt(freePeriodDays)
      });
      setFreePeriodMsg({ type: 'success', text: t('billing:subFreePeriodSuccess') });
      setFreePeriodDialog({ open: false, userId: null, name: '' });
      // Обновляем список клиентов
      const res = await api.get(`${API_BASE}/subscription/clients`);
      if (res.data?.success) setClients(res.data.clients || []);
    } catch (e) {
      setFreePeriodMsg({ type: 'error', text: t('billing:subFreePeriodError') });
    }
  };

  const handleToggleMonitoring = async () => {
    const { userId, targetDisabled } = monitoringDialog;
    if (!userId) return;
    try {
      setMonitoringBusy(true);
      await api.patch(`${API_BASE}/users/${userId}/monitoring-disabled`, { disabled: targetDisabled });
      const res = await api.get(`${API_BASE}/subscription/clients`);
      if (res.data?.success) setClients(res.data.clients || []);
      setMonitoringDialog({ open: false, userId: null, email: '', targetDisabled: false });
    } catch (e) {
      showError(t('billing:monitoringToggleError'));
    } finally {
      setMonitoringBusy(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    try {
      return new Date(iso).toLocaleDateString(getLocale());
    } catch (_) {
      return String(iso);
    }
  };

  const daysLeft = (iso) => {
    if (!iso) return null;
    const diff = new Date(iso) - new Date();
    if (diff <= 0) return 0;
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'free_period': return t('subStatusFreePeriod');
      case 'active': return t('subStatusActive');
      case 'grace': return t('subStatusGrace');
      case 'blocked': return t('subStatusBlocked');
      case 'expired': return t('subStatusExpired');
      case 'no_subscription': return t('subStatusNoSubscription');
      case 'no_devices': return t('subStatusNoDevices');
      case 'not_applicable': return t('subStatusNotApplicable');
      default: return status || '-';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'free_period': return 'info';
      case 'active': return 'success';
      case 'grace': return 'warning';
      case 'blocked': return 'error';
      case 'expired': return 'error';
      case 'no_subscription': return 'default';
      case 'no_devices': return 'default';
      default: return 'default';
    }
  };

  const renderSubBlock = ({ data, loading, error, paymentOpen, setPaymentOpen, onPaid, showTariffScale }) => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return <Typography color="error">{error}</Typography>;
    }

    if (!data || !data.success) {
      return <Typography color="text.secondary">{t('subNoData')}</Typography>;
    }

    if (data.status === 'not_applicable') {
      return <Typography color="text.secondary">{data.message}</Typography>;
    }

    if (data.status === 'no_devices') {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <DevicesIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography color="text.secondary">{t('subNoDevicesTitle')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('subNoDevicesDescription')}
          </Typography>
        </Box>
      );
    }

    const graceDays = daysLeft(data.grace_until);
    const freeDays = daysLeft(data.free_period_until);

    return (
      <>
        {/* Баннеры по статусу */}
        {data.status === 'blocked' && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {t('subBannerBlocked')}
          </Alert>
        )}
        {data.status === 'expired' && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {t('subBannerExpired')}
          </Alert>
        )}
        {data.status === 'no_subscription' && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {t('subBannerNoSubscription')}
          </Alert>
        )}

        {/* Статус подписки */}
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' }, rowGap: 2, columnGap: 2 }}>
            <Typography color="text.secondary">{t('labelStatus')}</Typography>
            <Box>
              <Chip label={getStatusLabel(data.status)} color={getStatusColor(data.status)} size="small" />
            </Box>

            {data.free_period_until && (
              <>
                <Typography color="text.secondary">{t('subFreePeriodUntil')}</Typography>
                <Typography>
                  {formatDate(data.free_period_until)}
                  {freeDays !== null && freeDays > 0 && (
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      ({t('subDaysLeft', { count: freeDays })})
                    </Typography>
                  )}
                </Typography>
              </>
            )}

            {data.paid_until && (
              <>
                <Typography color="text.secondary">{t('subPaidUntil')}</Typography>
                <Typography>{formatDate(data.paid_until)}</Typography>
              </>
            )}

            {data.grace_until && (
              <>
                <Typography color="text.secondary">{t('subGraceUntil')}</Typography>
                <Typography>
                  {formatDate(data.grace_until)}
                  {graceDays !== null && graceDays > 0 && (
                    <Typography component="span" variant="body2" color="warning.main" sx={{ ml: 1 }}>
                      ({t('subDaysRemaining', { count: graceDays })})
                    </Typography>
                  )}
                </Typography>
              </>
            )}
          </Box>
        </Paper>

        {/* Тариф */}
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>{t('subYourTariff')}</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' }, rowGap: 2, columnGap: 2 }}>
            <Typography color="text.secondary">{t('subDevices')}</Typography>
            <Typography>{data.device_count}</Typography>

            <Typography color="text.secondary">{t('subTariff')}</Typography>
            <Typography>${data.price_per_device_usd}{t('subPerDeviceMonth')}</Typography>

            <Typography color="text.secondary">{t('subAmount')}</Typography>
            <Typography>
              ${data.total_usd}
              {data.total_rub && (
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  (~{data.total_rub.toLocaleString(getLocale())} {t('subRubSign')})
                </Typography>
              )}
            </Typography>
          </Box>

          {data.pending_bill && (
            <Box sx={{ mt: 3, p: 2, borderRadius: 1, backgroundColor: theme.palette.action.hover }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {resolveBillName(data.pending_bill, t)}
                  </Typography>
                  <Typography variant="h6">
                    {renderBillAmount(data.pending_bill.amount, data.pending_bill.currency, data, { usdRubFromTariff: true })}
                  </Typography>
                  {data.pending_bill.grace_until && (
                    <Typography variant="body2" color="warning.main">
                      {t('subPayBefore', { date: formatDate(data.pending_bill.grace_until) })}
                    </Typography>
                  )}
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                  onClick={() => setPaymentOpen(true)}
                >
                  {t('goToPayment')}
                </Button>
              </Box>
            </Box>
          )}

          <PaymentDialog
            open={paymentOpen}
            onClose={() => setPaymentOpen(false)}
            billId={data.pending_bill?.id}
            onPaymentSuccess={(status) => {
              setPaymentOpen(false);
              if (typeof onPaid === 'function') onPaid();
            }}
          />
        </Paper>

        {showTariffScale && data.tariffs && data.tariffs.length > 0 && (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>{t('subTariffScale')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('subTariffScaleHint')}
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('subDevices')}</TableCell>
                  <TableCell align="right">{t('subPricePerUnit')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.tariffs.map((tier, idx) => {
                  const isCurrentTier = data.device_count >= tier.min && (tier.max === null || data.device_count <= tier.max);
                  return (
                    <TableRow
                      key={idx}
                      sx={isCurrentTier ? { backgroundColor: theme.palette.action.selected } : {}}
                    >
                      <TableCell>
                        {tier.max ? `${tier.min} - ${tier.max}` : `${tier.min}+`}
                        {isCurrentTier && (
                          <Chip label={t('subCurrentTariff')} size="small" color="primary" variant="outlined" sx={{ ml: 1 }} />
                        )}
                      </TableCell>
                      <TableCell align="right">${tier.price}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </Box>
          </Paper>
        )}

        {/* История платежей */}
        {data.history && data.history.length > 0 && (
          <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>{t('subPaymentHistory')}</Typography>
            <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('labelTitle')}</TableCell>
                  <TableCell>{t('labelIssuedDate')}</TableCell>
                  <TableCell>{t('labelPaidDate')}</TableCell>
                  <TableCell align="right">{t('labelAmount')}</TableCell>
                  <TableCell>{t('labelStatus')}</TableCell>
                  <TableCell align="center">PDF</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.history.map((bill) => (
                  <TableRow key={bill.id}>
                    <TableCell>{resolveBillName(bill, t)}</TableCell>
                    <TableCell>{formatDate(bill.date_create)}</TableCell>
                    <TableCell>{formatDate(bill.date_payment)}</TableCell>
                    <TableCell align="right">
                      {renderBillAmount(bill.amount, bill.currency, data)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={bill.status === 'paid' ? t('statusPaid') : bill.status === 'overdue' ? t('statusOverdue') : bill.status}
                        color={bill.status === 'paid' ? 'success' : bill.status === 'overdue' ? 'error' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={t('downloadPdf')}>
                        <IconButton
                          size="small"
                          onClick={async () => {
                            try {
                              const res = await api.get(`${API_BASE}/bill/${bill.id}/pdf`, { responseType: 'blob' });
                              const blob = new Blob([res.data], { type: 'application/pdf' });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `bill_${bill.id.substring(0, 8)}.pdf`;
                              a.click();
                              URL.revokeObjectURL(url);
                            } catch (e) {
                              if (e?.response?.status === 412) {
                                showError(t('billing:requisitesMissing'));
                              } else {
                                console.error('Subscription: ошибка скачивания PDF', e);
                              }
                            }
                          }}
                        >
                          <PictureAsPdfIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </Box>
          </Paper>
        )}
      </>
    );
  };

  const hideOwnSubscription = isAdmin || (isHoster && data?.status === 'no_devices');
  const showHosterBlock = isHoster && hosterData && hosterData.status !== 'not_applicable';

  return (
    <>
      <Menu />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {!hideOwnSubscription && (
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <CardMembershipIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
              <Typography variant="h4" component="h1">
                {t('subPageTitle')}
              </Typography>
            </Box>
            {renderSubBlock({
              data,
              loading,
              error,
              paymentOpen,
              setPaymentOpen,
              onPaid: fetchSubscription,
              showTariffScale: true
            })}
          </Paper>
        )}

        {showHosterBlock && (
          <Paper sx={{ p: 3, mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <CardMembershipIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
              <Typography variant="h4" component="h1">
                {t('subHosterPageTitle')}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('subHosterIntro', { count: hosterData?.hosting_count || 0 })}
            </Typography>
            {renderSubBlock({
              data: hosterData,
              loading: hosterLoading,
              error: hosterError,
              paymentOpen: hosterPaymentOpen,
              setPaymentOpen: setHosterPaymentOpen,
              onPaid: fetchHosterSubscription,
              showTariffScale: !data || hideOwnSubscription
            })}
          </Paper>
        )}

        {isAdmin && (
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h5" sx={{ mb: 3 }}>{t('billing:subClientsTitle')}</Typography>
            {freePeriodMsg && (
              <Alert severity={freePeriodMsg.type} sx={{ mb: 2 }} onClose={() => setFreePeriodMsg(null)}>
                {freePeriodMsg.text}
              </Alert>
            )}
            {clientsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : clients.length === 0 ? (
              <Typography color="text.secondary">{t('billing:subNoData')}</Typography>
            ) : (() => {
              const q = clientsSearchQuery.trim().toLowerCase();
              const filteredClients = q
                ? clients.filter(c => (c.name || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q))
                : clients;
              return (
              <Box sx={{ overflowX: 'auto' }}>
              <TextField
                size="small"
                fullWidth
                placeholder={t('billing:subClientsSearchPlaceholder')}
                value={clientsSearchQuery}
                onChange={(e) => setClientsSearchQuery(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('profile:labelName')}</TableCell>
                    <TableCell>{t('billing:adminLabelEmail')}</TableCell>
                    <TableCell align="center">{t('billing:subDevices')}</TableCell>
                    <TableCell>{t('billing:subColStatusSubscription')}</TableCell>
                    <TableCell align="right">{t('billing:subAmount')}</TableCell>
                    {isAdmin && <TableCell align="center">{t('billing:labelActions')}</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredClients.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography color="text.secondary" sx={{ py: 2 }}>
                          {t('billing:subClientsNoMatch')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : filteredClients.map((c) => (
                    <TableRow key={c.user_id}>
                      <TableCell>{c.name || '-'}</TableCell>
                      <TableCell>{c.email || '-'}</TableCell>
                      <TableCell align="center">{c.device_count}</TableCell>
                      <TableCell>
                        <Chip label={getStatusLabel(c.status)} color={getStatusColor(c.status)} size="small" />
                      </TableCell>
                      <TableCell align="right">
                        {c.amount != null
                          ? (c.status === 'no_subscription'
                              ? `≈ ${formatBillAmount(c.amount, c.currency || 'RUB', t)}`
                              : formatBillAmount(c.amount, c.currency || 'RUB', t))
                          : '-'}
                      </TableCell>
                      {isAdmin && (
                        <TableCell align="center">
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                              <Tooltip title={c.monitoring_disabled ? t('billing:monitoringSwitchTooltipEnable') : t('billing:monitoringSwitchTooltipDisable')}>
                                <Switch
                                  size="small"
                                  checked={!c.monitoring_disabled}
                                  onChange={() => setMonitoringDialog({
                                    open: true,
                                    userId: c.user_id,
                                    email: c.email || c.name || c.user_id,
                                    targetDisabled: !c.monitoring_disabled
                                  })}
                                />
                              </Tooltip>
                              <Typography variant="caption" color="text.secondary">
                                {t('billing:colMonitoringAccess')}
                              </Typography>
                            </Box>
                            <Tooltip title={t('billing:subSetFreePeriod')}>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setFreePeriodDays(7);
                                  setFreePeriodDialog({ open: true, userId: c.user_id, name: c.name || c.email });
                                }}
                              >
                                <CardGiftcardIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </Box>
              );
            })()}
          </Paper>
        )}

        {/* Диалог установки бесплатного периода */}
        <Dialog open={freePeriodDialog.open} onClose={() => setFreePeriodDialog({ open: false, userId: null, name: '' })} fullWidth maxWidth="xs">
          <DialogTitle>{t('billing:subSetFreePeriod')}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>{freePeriodDialog.name}</Typography>
            <TextField
              type="number"
              label={t('billing:subFreePeriodDays')}
              value={freePeriodDays}
              onChange={(e) => setFreePeriodDays(e.target.value)}
              inputProps={{ min: 1, max: 365 }}
              fullWidth
              size="small"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFreePeriodDialog({ open: false, userId: null, name: '' })}>
              {t('billing:cancelButton')}
            </Button>
            <Button variant="contained" onClick={handleSetFreePeriod}>
              {t('common:confirm')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={monitoringDialog.open}
          onClose={() => !monitoringBusy && setMonitoringDialog({ open: false, userId: null, email: '', targetDisabled: false })}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>
            {monitoringDialog.targetDisabled ? t('billing:monitoringDialogTitleDisable') : t('billing:monitoringDialogTitleEnable')}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              {monitoringDialog.targetDisabled
                ? t('billing:monitoringDisableDialog', { email: monitoringDialog.email })
                : t('billing:monitoringEnableDialog', { email: monitoringDialog.email })}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('billing:monitoringDialogAdminOverrideHint')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setMonitoringDialog({ open: false, userId: null, email: '', targetDisabled: false })}
              disabled={monitoringBusy}
            >
              {t('billing:cancelButton')}
            </Button>
            <Button
              variant="contained"
              onClick={handleToggleMonitoring}
              disabled={monitoringBusy}
            >
              {t('common:confirm')}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

export default Subscription;
